import { motion } from "framer-motion";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Phone, Mail, MapPin, Clock, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { SiteLayout } from "@/components/site/SiteLayout";
import calendrierWhite from "@/assets/icons/calendrier-white.webp";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema } from "@/components/SchemaOrg";
import accueilReception from "@/assets/centre/accueil-reception.webp";

// Custom vehicle icons
import voitureRed from "@/assets/icons/voiture-red.webp";
import motoRed from "@/assets/icons/moto-red.webp";
import utilitaireRed from "@/assets/icons/utilitaire-red.webp";
import collectionRed from "@/assets/icons/collection-red.webp";
import fourxfourRed from "@/assets/icons/4x4-red.webp";

import gplRed from "@/assets/icons/gpl-red.webp";
import specifiqueRed from "@/assets/icons/specifique-red.webp";

const ContactPage = () => {
  const [showAllVehicles, setShowAllVehicles] = useState(false);

  const isMobile = useIsMobile();

  const prestations = [
    { icon: motoRed, label: "2/3 Roues", subtitle: "Motos, quads, voiturettes", href: "/vehicules/motos" },
    { icon: collectionRed, label: "Collection", subtitle: "Anciennes et exception", href: "/vehicules/collection" },
    { icon: voitureRed, label: "Voiture", subtitle: "Véhicules particuliers", href: "/vehicules/voitures" },
    { icon: fourxfourRed, label: "Véhicule 4x4", subtitle: "SUV et tout-terrain", href: "/vehicules/voitures" },
    { icon: utilitaireRed, label: "Utilitaire", subtitle: "Fourgons, camionnettes", href: "/vehicules/utilitaires" },
    { icon: specifiqueRed, label: "Spécifique", subtitle: "Camping-cars, aménagés", href: "/vehicules/utilitaires" },
    { icon: gplRed, label: "Véhicule GPL", subtitle: "GPL / GNV", href: "/vehicules/voitures" },
  ];

  const visiblePrestations = isMobile && !showAllVehicles ? prestations.slice(0, 4) : prestations;

  const coordonnees = [
    {
      icon: Phone,
      title: "Téléphone",
      value: "01 49 88 92 40",
      href: "tel:+33149889240",
    },
    {
      icon: Mail,
      title: "Email",
      value: "autosur.montreuil@gmail.com",
      href: "mailto:autosur.montreuil@gmail.com",
    },
    {
      icon: MapPin,
      title: "Adresse",
      value: "133, Bd Aristide Briand\n93100 Montreuil",
      href: "https://maps.app.goo.gl/QMbfid7TsPVr8ZB76",
    },
    {
      icon: Clock,
      title: "Horaires",
      value: "Lun-Ven: 8h30-18h30\nSam: 9h-13h",
      href: "https://controle-technique.autosur.fr/550-autosur-montreuil-sous-bois#in-nav-rdv",
    },
  ];

  return (
    <SiteLayout>
      <SEOHead
        title="Contact et RDV en ligne"
        description="Prenez RDV en ligne 24/7 ou appelez le 01 49 88 92 40. Adresse : 133 Bd Aristide Briand, 93100 Montreuil. Parking gratuit, accès PMR."
        path="/contact"
      />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "Contact", href: "/contact" }]} />
      {/* Hero Banner */}
      <section className="relative py-16 md:py-20 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${accueilReception})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              CONTACT
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Prenez rendez-vous
            </h1>
            <p className="font-body text-white/80 text-lg">
              Contactez-nous pour réserver votre contrôle technique. Nous vous répondrons dans les plus brefs délais.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Prise de RDV Section */}
      <section className="py-12 md:py-16 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-5xl mx-auto"
          >
            <a
              href="https://controle-technique.autosur.fr/550-autosur-montreuil-sous-bois#in-nav-rdv"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden hover:shadow-xl hover:border-autosur-blue/30 transition-all group"
            >
              <div className="p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
                {/* Offre WEB */}
                <div className="flex-shrink-0 bg-autosur-grey-light rounded-xl p-6 text-center w-full md:w-56">
                  <p className="font-heading font-bold text-autosur-navy text-lg mb-3">OFFRE WEB</p>
                  <p className="font-heading font-bold text-autosur-red text-sm mb-4">TARIF PRÉFÉRENTIEL</p>
                  <div className="bg-autosur-red rounded-lg px-4 py-3 mb-1">
                    <p className="text-white text-xs font-body"><span className="line-through">80€</span> À partir de</p>
                    <p className="text-white font-heading font-bold text-2xl">73,00 €</p>
                  </div>
                  <p className="font-body text-muted-foreground text-[11px]">
                    (*) Offre non cumulable
                  </p>
                </div>

                {/* CTA */}
                <div className="text-center md:text-left flex-1">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-3">
                    Réservez votre contrôle technique en ligne
                  </h2>
                  <p className="font-body text-muted-foreground mb-6">
                    Sélectionnez votre créneau directement sur notre plateforme de réservation et profitez de nos tarifs préférentiels.
                  </p>
                  <span className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-autosur-red text-white font-heading font-semibold rounded-full group-hover:bg-autosur-red-light transition-all shadow-lg group-hover:shadow-xl">
                    <img src={calendrierWhite} alt="" className="w-6 h-6" />
                    Prendre RDV
                  </span>
                </div>
              </div>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Prestations + Localisation */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Nos prestations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-autosur-red font-heading font-semibold tracking-widest text-xs mb-3 block">
                NOS PRESTATIONS
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-4">
                Types de véhicules
              </h2>
              <p className="font-body text-muted-foreground mb-6">
                Notre centre est agréé pour le contrôle technique de tous types de véhicules légers.
              </p>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {visiblePrestations.map((item, index) => (
                  <Link
                    key={index}
                    to={item.href}
                    className="flex items-center gap-2 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-autosur-blue/30 hover:shadow-md transition-all group"
                  >
                    <img src={item.icon} alt="" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="font-heading font-semibold text-autosur-navy text-sm sm:text-base leading-tight">{item.label}</span>
                      <span className="font-body text-muted-foreground text-[10px] sm:text-xs leading-tight">{item.subtitle}</span>
                    </div>
                  </Link>
                ))}
              </div>

              {isMobile && !showAllVehicles && (
                <button
                  onClick={() => setShowAllVehicles(true)}
                  className="mt-4 w-full py-3 text-center font-heading font-semibold text-autosur-blue hover:text-autosur-navy transition-colors flex items-center justify-center gap-2"
                >
                  Voir plus
                  <ChevronDown className="w-4 h-4" />
                </button>
              )}
            </motion.div>

            {/* Localisation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <span className="text-autosur-red font-heading font-semibold tracking-widest text-xs mb-3 block">
                LOCALISATION
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-4">
                Nous trouver
              </h2>
              <p className="font-body text-muted-foreground mb-6">
                Le centre est situé au 133 Boulevard Aristide Briand, à Montreuil-sous-Bois.
              </p>

              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.215933153219!2d2.4535329999999997!3d48.873160000000006!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66d4a173a4aa9%3A0xd973599b099949f6!2sAUTOSUR%20MOTOSUR%20Contr%C3%B4le%20Technique%20AUTO%20%26%20MOTO!5e0!3m2!1sfr!2sfr!4v1770576435387!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation Autosur Montreuil-sous-Bois"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coordonnées */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-xs mb-3 block">
              CONTACT
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy">
              Nos Coordonnées
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {coordonnees.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                {item.href ? (
                  <a
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="block bg-white rounded-2xl p-6 text-center border border-gray-100 hover:shadow-lg hover:border-autosur-blue/30 transition-all h-full"
                  >
                    <div className="w-14 h-14 mx-auto mb-4 bg-autosur-grey-light rounded-full flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-autosur-navy" />
                    </div>
                    <h3 className="font-heading font-bold text-autosur-navy text-sm mb-2">{item.title}</h3>
                    <p className="font-body text-muted-foreground text-sm whitespace-pre-line">{item.value}</p>
                  </a>
                ) : (
                  <div className="block bg-white rounded-2xl p-6 text-center border border-gray-100 h-full">
                    <div className="w-14 h-14 mx-auto mb-4 bg-autosur-grey-light rounded-full flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-autosur-navy" />
                    </div>
                    <h3 className="font-heading font-bold text-autosur-navy text-sm mb-2">{item.title}</h3>
                    <p className="font-body text-muted-foreground text-sm whitespace-pre-line">{item.value}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Urgente */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative bg-autosur-grey-light rounded-2xl p-8 md:p-10">
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-autosur-navy rounded-tl-2xl" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-autosur-navy rounded-br-2xl" />
              
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-autosur-navy mb-2">
                    Une demande urgente ?
                  </h3>
                  <p className="font-body text-muted-foreground">
                    Appelez-nous directement au{" "}
                    <a href="tel:+33149889240" className="text-autosur-red font-semibold hover:underline">
                      01 49 88 92 40
                    </a>
                  </p>
                </div>
                <a
                  href="tel:+33149889240"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-autosur-navy text-autosur-navy font-heading font-semibold rounded-full hover:bg-autosur-navy hover:text-white transition-all whitespace-nowrap"
                >
                  <img src={calendrierWhite} alt="" className="w-5 h-5 hidden group-hover:block" />
                  <Phone className="w-5 h-5" />
                  Appeler maintenant
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default ContactPage;
